// Help & FAQs — corpus, categories and search (docs/HELP_SCOPE.md).
//
// The corpus is CMS-managed (`faq` area, seeded from cms-seed/faq.json). BAKED_FAQS
// below is the fallback, exactly like every ct() call site's inline default: if the
// CMS bundle has no faq entries — first load, offline before any publish, a fresh
// environment — Help still works, just with whatever was true at build time. Once
// entries are published they replace the baked set WHOLESALE, not per-entry: mixing
// a stale baked answer in among edited ones is how the Savings Vault answer stayed
// wrong for weeks after W1.6 removed the bank-account concept it described. Same
// collection-level switch cmsWordlistCategories uses, same reasoning.
//
// CATEGORIES are a fixed registry here rather than CMS data. An entry naming a
// category that doesn't exist would otherwise vanish from the UI with no error, and
// re-ordering categories from a text field is a foot-gun. Their LABELS are ct() keys,
// so the words are still editable without a deploy — only the set and the order are code.
//
// Deliberately near-dependency-free (content.js only, itself import-free) so this is
// safe to import from anywhere.

import { cmsFaqEntries, t as ct } from "./content.js";

// Plain-English, question-shaped names. Deliberately NOT the app's own vocabulary:
// a child looking for the Oink Shop doesn't think "Rewards & Gamification", and a
// parent looking for the exchange rate doesn't think "Reward settings". The test
// applied to each: could someone who has never used the app guess which one to open?
export const FAQ_CATEGORIES = [
  { id: "start", fallback: "Getting started" },
  { id: "earning", fallback: "Earning money" },
  { id: "saving", fallback: "Saving up" },
  { id: "spending", fallback: "Spending it" },
  { id: "games", fallback: "Games, Piggington and prizes" },
  { id: "setup", fallback: "Setting things up" },
  { id: "devices", fallback: "Phones, tablets and alerts" },
  { id: "access", fallback: "Codes, PINs and getting back in" },
  { id: "privacy", fallback: "Your data and privacy" },
  { id: "problems", fallback: "When something's not right" },
];

const CATEGORY_IDS = new Set(FAQ_CATEGORIES.map((c) => c.id));

export function faqCategoryLabel(id) {
  const cat = FAQ_CATEGORIES.find((c) => c.id === id);
  if (!cat) return id;
  return ct(`faq.cat.${cat.id}`, cat.fallback);
}

// Baked fallback. Kept intentionally shorter than the CMS corpus — this is the
// "nothing published yet" safety net, not a second source of truth. cms-seed/faq.json
// is the real corpus; if you're adding an answer, add it there.
export const BAKED_FAQS = [
  { id: "faq.start.what_is_it", category: "start", audience: "both", sortOrder: 10,
    tags: ["what is", "about", "how it works", "pocket money"],
    q: "What is Little Bankers?",
    a: "It's a family app where kids earn, save and spend. A grown-up sets up jobs and rewards, you earn tokens for doing them, and you save up for things you want.\n\nThere's no card and no real bank account. The money stays with your grown-up, and this app keeps score." },
  { id: "faq.earning.tokens", category: "earning", audience: "both", sortOrder: 10,
    tags: ["tokens", "coins", "earn", "money"],
    q: "How do I earn tokens?",
    a: "By doing jobs a grown-up has set up, ticking off your daily tasks, and playing the Learn & Earn games. A grown-up can also add tokens whenever they like." },
  { id: "faq.earning.limit", category: "earning", audience: "kid", sortOrder: 80,
    tags: ["limit", "cap", "reached", "stopped earning"],
    q: "Why does it say I've reached my limit for the week?",
    a: "Some families set a limit on how much can be earned in one week. You can still play, and you'll still collect Oinks and trophies. New tokens just pause until the limit starts again." },
  { id: "faq.saving.vault_kid", category: "saving", audience: "kid", sortOrder: 10,
    tags: ["vault", "savings", "saving", "pounds"],
    q: "What is my Savings Vault?",
    a: "It's where your longer-term savings live, kept in pounds instead of tokens. Your grown-up looks after that money for you in real life. The Vault is the agreed record of how much is yours." },
  { id: "faq.saving.goals", category: "saving", audience: "kid", sortOrder: 70,
    tags: ["goal", "goals", "saving up", "target"],
    q: "What's a goal?",
    a: "Something you're saving up for. Goals use your tokens first, then top up from your Savings Vault. When you've got enough, a Redeem button appears." },
  { id: "faq.games.oinks", category: "games", audience: "kid", sortOrder: 10,
    tags: ["oinks", "op", "pink", "points"],
    q: "What are Oinks?",
    a: "Oinks are your fun points: the pink squares, never the gold coins. You get them for playing Learn & Earn, and you spend them in the Oink Shop. They can't be swapped for tokens or real money." },
  { id: "faq.devices.install", category: "devices", audience: "both", sortOrder: 10,
    tags: ["install", "home screen", "app"],
    q: "Can I put Little Bankers on my home screen?",
    a: "Yes. On Android, tap the Install button near the top. On iPhone or iPad, open the Share menu in Safari and choose \"Add to Home Screen\"." },
  { id: "faq.access.code", category: "access", audience: "both", sortOrder: 10,
    tags: ["household code", "forgot", "lost"],
    q: "I've forgotten our household code.",
    a: "A grown-up can find it in Parent Mode → Settings → Household." },
  { id: "faq.access.pin", category: "access", audience: "parent", sortOrder: 30,
    tags: ["pin", "forgot", "reset", "locked out"],
    q: "I've forgotten the Parent PIN.",
    a: "On the PIN screen, tap \"Forgotten your PIN?\" and enter the email address the household was set up with. This doesn't affect the household passcode." },
  { id: "faq.problems.report", category: "problems", audience: "both", sortOrder: 10,
    tags: ["report", "bug", "problem", "confusing", "feedback"],
    q: "Something's broken, confusing, or just annoying. How do I tell you?",
    a: "Use the button at the top of this page. You don't need to have found a bug: \"this doesn't make sense\" is just as useful to us." },
];

// LB-8: which answers the PUBLIC help page (/help, worker-rendered) shows. `public` is an
// optional boolean on the entry; absent means true for a parent answer and false for both/kid.
// A kid-audience answer is never public whatever the field says (the worker's validator
// refuses public: true on one, and this is the belt to that brace). The in-app Help sheet
// does not read this — it shows every answer for the audience on the device.
export function faqIsPublic(entry) {
  if (!entry || entry.audience === "kid") return false;
  if (typeof entry.public === "boolean") return entry.public;
  return entry.audience === "parent";
}

// Exported for the worker's /help page (LB-8), which must shape a published row exactly as
// the app does; the in-app callers keep the private name below.
export function normaliseFaqEntry(raw) {
  return normaliseEntry(raw);
}

function normaliseEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const v = raw.value && typeof raw.value === "object" ? raw.value : raw;
  const q = typeof v.q === "string" ? v.q.trim() : "";
  const a = typeof v.a === "string" ? v.a.trim() : "";
  if (!q || !a) return null;
  if (v.retired) return null;
  const category = CATEGORY_IDS.has(v.category) ? v.category : "problems";
  const audience = v.audience === "kid" || v.audience === "parent" ? v.audience : "both";
  return {
    id: typeof raw.id === "string" ? raw.id : q,
    q,
    a,
    category,
    audience,
    sortOrder: typeof v.sortOrder === "number" ? v.sortOrder : 0,
    tags: Array.isArray(v.tags) ? v.tags.filter((x) => typeof x === "string") : [],
    // Carried through for faqIsPublic (LB-8); undefined when the field is absent, on purpose,
    // so "absent" keeps its own meaning (the audience default) rather than becoming false.
    public: typeof v.public === "boolean" ? v.public : undefined,
  };
}

// The full corpus for this session: CMS if anything is published, baked set otherwise.
// Never throws — Help is the screen someone reaches when everything else has already
// gone wrong, so it must not be able to fail.
export function allFaqs() {
  try {
    const published = cmsFaqEntries();
    const source = published && published.length ? published : BAKED_FAQS;
    return source
      .map(normaliseEntry)
      .filter(Boolean)
      .sort((x, y) => x.sortOrder - y.sortOrder || x.q.localeCompare(y.q));
  } catch {
    return BAKED_FAQS.map(normaliseEntry).filter(Boolean);
  }
}

// "kid" | "parent" | "all". "all" is the escape hatch for a parent looking for
// something a child asked about, and for anyone the audience guess got wrong.
export function faqsForAudience(entries, audience) {
  if (audience === "all") return entries;
  return entries.filter((e) => e.audience === "both" || e.audience === audience);
}

// Lowercase, strip accents, collapse anything that isn't a letter or digit. Means
// "PIN?", "pin" and "P.I.N." all reach the same tokens, and a child typing with no
// punctuation at all still matches.
function norm(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function tokens(s) {
  const n = norm(s);
  return n ? n.split(" ") : [];
}

// Scores one entry against one query token. Ordered so that a deliberate tag beats an
// accidental word in an answer: tags are the synonym layer (a child types "coins",
// the entry is titled "tokens"), and they're the only reason free-text search works
// on a corpus this small.
function scoreToken(entry, token) {
  const tagText = norm(entry.tags.join(" "));
  const qText = norm(entry.q);
  const aText = norm(entry.a);
  const tagWords = tagText ? tagText.split(" ") : [];
  const qWords = qText ? qText.split(" ") : [];

  if (tagWords.includes(token)) return 6;
  if (tagText.includes(token) && token.length >= 3) return 4;
  if (qWords.includes(token)) return 3;
  if (qWords.some((w) => w.startsWith(token)) && token.length >= 3) return 2;
  if (aText.includes(token) && token.length >= 3) return 1;
  return 0;
}

// AND semantics: every token in the query has to hit something, or the entry is out.
// Two-word queries ("kid notifications") should narrow, not widen — an OR search on a
// 70-entry corpus returns most of it and is worse than no search at all.
export function searchFaqs(entries, query) {
  const raw = tokens(query);
  // Single letters are dropped: on a corpus this small an "a" or "s" matches almost
  // everything and makes the AND rule useless. But that alone loses genuinely typed
  // acronyms — "P.I.N." and "T.T." normalise to three and two one-letter tokens and
  // would otherwise search for nothing at all. When EVERY token is a single letter,
  // treat the run as one word instead.
  const qTokens = raw.filter((tk) => tk.length > 1);
  if (!qTokens.length && raw.length > 1 && raw.every((tk) => tk.length === 1)) {
    qTokens.push(raw.join(""));
  }
  if (!qTokens.length) return null; // null means "not searching", distinct from "no results"
  const scored = [];
  for (const entry of entries) {
    let total = 0;
    let allHit = true;
    for (const tk of qTokens) {
      const s = scoreToken(entry, tk);
      if (!s) { allHit = false; break; }
      total += s;
    }
    if (allHit) scored.push({ entry, score: total });
  }
  scored.sort((x, y) => y.score - x.score || x.entry.sortOrder - y.entry.sortOrder);
  return scored.map((s) => s.entry);
}

// Groups into the fixed category order, dropping empty categories. Used for the
// browse view; search results are shown flat, because once someone has typed a query
// the category is noise.
export function groupByCategory(entries) {
  return FAQ_CATEGORIES.map((cat) => ({
    id: cat.id,
    label: faqCategoryLabel(cat.id),
    items: entries.filter((e) => e.category === cat.id),
  })).filter((g) => g.items.length);
}
